import { useState, useEffect, useCallback, useMemo } from 'react';
import customGroupsService, { CustomGroupError } from '../services/customGroupsService';

/**
 * Custom hook for managing custom groups
 * Provides state management, operations, and error handling for custom groups
 */
export default function useCustomGroups() {
  const [customGroups, setCustomGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Load groups on mount
  useEffect(() => {
    loadCustomGroups();
  }, []);

  /**
   * Loads all custom groups from storage
   */
  const loadCustomGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedGroups = await customGroupsService.getAll();
      setCustomGroups(loadedGroups);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load custom groups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Creates a new custom group
   * @param {string} name - Group name
   * @param {Array<string>} countries - Array of country ISO codes
   * @returns {Promise<Object|null>} The created group or null if failed
   */
  const createCustomGroup = useCallback(async (name, countries) => {
    try {
      setOperationInProgress(true);
      setError(null);

      const newGroup = await customGroupsService.create(name, countries);

      // Update local state
      setCustomGroups((prev) => ({
        ...prev,
        [newGroup.id]: newGroup,
      }));

      return newGroup;
    } catch (err) {
      setError(err.message);

      // Return error details for component-level handling
      if (err instanceof CustomGroupError) {
        throw err;
      }
      throw new Error('Failed to create group');
    } finally {
      setOperationInProgress(false);
    }
  }, []);

  /**
   * Updates an existing custom group
   * @param {string} id - Group ID
   * @param {string} name - New group name
   * @param {Array<string>} countries - Array of country ISO codes
   * @returns {Promise<Object|null>} The updated group or null if failed
   */
  const updateCustomGroup = useCallback(async (id, name, countries) => {
    try {
      setOperationInProgress(true);
      setError(null);

      const updatedGroup = await customGroupsService.update(id, name, countries);

      // Update local state
      setCustomGroups((prev) => ({
        ...prev,
        [id]: updatedGroup,
      }));

      return updatedGroup;
    } catch (err) {
      setError(err.message);

      // Return error details for component-level handling
      if (err instanceof CustomGroupError) {
        throw err;
      }
      throw new Error('Failed to update group');
    } finally {
      setOperationInProgress(false);
    }
  }, []);

  /**
   * Deletes a custom group
   * @param {string} id - Group ID to delete
   * @returns {Promise<boolean>} True if successful
   */
  const deleteCustomGroup = useCallback(async (id) => {
    try {
      setOperationInProgress(true);
      setError(null);

      await customGroupsService.delete(id);

      // Update local state
      setCustomGroups((prev) => {
        const newGroups = { ...prev };
        delete newGroups[id];
        return newGroups;
      });

      return true;
    } catch (err) {
      setError(err.message);

      // Return error details for component-level handling
      if (err instanceof CustomGroupError) {
        throw err;
      }
      throw new Error('Failed to delete group');
    } finally {
      setOperationInProgress(false);
    }
  }, []);

  /**
   * Gets a custom group by ID
   * @param {string} id - Group ID
   * @returns {Object|null} The group or null if not found
   */
  const getCustomGroupById = useCallback(
    (id) => {
      return customGroups[id] || null;
    },
    [customGroups],
  );

  /**
   * Gets a custom group by name
   * @param {string} name - Group name
   * @returns {Object|null} The group or null if not found
   */
  const getCustomGroupByName = useCallback(
    (name) => {
      return Object.values(customGroups).find((group) => group.name === name) || null;
    },
    [customGroups],
  );

  /**
   * Checks if a custom group name already exists
   * @param {string} name - Group name to check
   * @param {string} excludeId - Optional ID to exclude from check (for updates)
   * @returns {boolean} True if name exists
   */
  const customGroupNameExists = useCallback(
    (name, excludeId = null) => {
      return Object.values(customGroups).some((group) => group.name === name && group.id !== excludeId);
    },
    [customGroups],
  );

  /**
   * Creates a custom group from URL parameters (for sharing)
   * @param {string} urlParam - The URL parameter containing group data
   * @returns {Promise<Object|null>} The created/existing group or null if invalid
   */
  const createCustomGroupFromUrl = useCallback(
    async (urlParam) => {
      try {
        const parsed = customGroupsService.urlUtils.parseUrlParam(urlParam);
        if (!parsed) {
          return null;
        }

        const { id, name, countries } = parsed;

        // Check if group already exists
        if (customGroups[id]) {
          return customGroups[id];
        }

        setOperationInProgress(true);
        setError(null);

        const newGroup = await customGroupsService.createFromUrl(id, name, countries);

        // Update local state
        setCustomGroups((prev) => ({
          ...prev,
          [newGroup.id]: newGroup,
        }));

        return newGroup;
      } catch (err) {
        setError(err.message);
        return null;
      } finally {
        setOperationInProgress(false);
      }
    },
    [customGroups],
  );

  /**
   * Generates a URL parameter for sharing a custom group
   * @param {string} groupId - Group ID
   * @returns {string|null} The URL parameter or null if group not found
   */
  const generateCustomGroupUrlParam = useCallback(
    (groupId) => {
      const group = customGroups[groupId];
      if (!group) {
        return null;
      }

      return customGroupsService.urlUtils.generateUrlParam(group.id, group.name, group.countries);
    },
    [customGroups],
  );

  /**
   * Validates a custom group name
   * @param {string} name - Name to validate
   * @returns {Object} Validation result { isValid: boolean, error: string|null }
   */
  const validateCustomGroupName = useCallback((name) => {
    return customGroupsService.validation.validateGroupName(name);
  }, []);

  /**
   * Validates country codes for custom groups
   * @param {Array<string>} countries - Countries to validate
   * @returns {Object} Validation result { isValid: boolean, error: string|null }
   */
  const validateCustomGroupCountries = useCallback((countries) => {
    return customGroupsService.validation.validateCountries(countries);
  }, []);

  // Memoize custom groups array for performance
  const customGroupsArray = useMemo(() => Object.values(customGroups), [customGroups]);

  // Memoize custom groups count
  const customGroupsCount = useMemo(() => Object.keys(customGroups).length, [customGroups]);

  return {
    // State
    customGroups,
    customGroupsArray,
    customGroupsCount,
    customGroupsLoading: loading,
    customGroupsError: error,
    customGroupsOperationInProgress: operationInProgress,

    // Operations
    createCustomGroup,
    updateCustomGroup,
    deleteCustomGroup,
    loadCustomGroups,

    // Queries
    getCustomGroupById,
    getCustomGroupByName,
    customGroupNameExists,

    // URL handling
    createCustomGroupFromUrl,
    generateCustomGroupUrlParam,
    parseCustomGroupUrlParam: customGroupsService.urlUtils.parseUrlParam,

    // Validation
    validateCustomGroupName,
    validateCustomGroupCountries,

    // Constants
    MAX_CUSTOM_GROUP_NAME_LENGTH: customGroupsService.constants.MAX_GROUP_NAME_LENGTH,
  };
}
